import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import AuthContext from '../context/AuthContext';
import axios from 'axios';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
});

const Login = () => {
  const { login } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the redirect path from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/';
  
  console.log('Redirect path after login will be:', from);
  
  // Check for existing token on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      console.log('Login page: Found existing auth data, redirecting to dashboard');
      window.location.href = '/';
    }
  }, []);

  // Special direct login for testing
  const forceDirect = () => {
    console.log('Using force direct login to dashboard');
    setLoginStatus('Direct login successful! Redirecting...');
    
    // First, clear any existing auth data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    
    // Store auth data directly
    const userData = {
      id: 'peter-id',
      name: 'Peter',
      email: 'peter@infanion.com',
      role: 'admin'
    };
    const token = 'direct-login-token-' + new Date().getTime();
    
    // Store in localStorage - MUST happen before page navigation
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isAuthenticated', 'true');
    
    console.log('Auth data set, checking values:');
    console.log('token:', localStorage.getItem('token'));
    console.log('user:', localStorage.getItem('user'));
    console.log('isAuthenticated:', localStorage.getItem('isAuthenticated'));
    
    // Force a complete page reload to reset React state
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  };
  
  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      setLoginStatus('Submitting login information...');
      console.log('Login form submitted with values:', { email: values.email, passwordLength: values.password?.length });
      
      // Ensure we're submitting valid data
      if (!values.email || !values.password) {
        console.error('Missing email or password');
        setLoginStatus('Error: Missing email or password');
        setIsSubmitting(false);
        return;
      }
      
      console.log('Calling login function with credentials');
      setLoginStatus('Contacting server...');
      
      // Try direct API call first
      try {
        // First try direct API call
        const apiUrl = 'http://localhost:5002/api/auth/login';
        console.log('Making direct API call to:', apiUrl);
        
        const response = await axios.post(apiUrl, values, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        console.log('Direct API response:', response);
        
        if (response.data && response.data.success) {
          // Set auth data in localStorage
          console.log('Setting auth data from direct API call');
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          localStorage.setItem('isAuthenticated', 'true');
          
          setLoginStatus('Login successful! Redirecting...');
          
          // Immediately check that data was set
          const tokenCheck = localStorage.getItem('token');
          const userCheck = localStorage.getItem('user');
          console.log('Verification - token exists:', !!tokenCheck);
          console.log('Verification - user exists:', !!userCheck);
          
          // Wait a moment to ensure data is saved
          setTimeout(() => {
            console.log('Redirecting with window.location');
            window.location.href = '/';
          }, 500);
          
          return;
        }
      } catch (directApiError) {
        console.error('Direct API call failed, falling back to context login');
        console.error('Error details:', directApiError);
      }
      
      // Attempt login with context login function as fallback
      try {
        const success = await login(values);
        console.log('Context login result:', success);
        
        if (success) {
          setLoginStatus('Login successful via context! Redirecting...');
          // Force immediate navigation to dashboard
          console.log('Login successful, force navigation');
          
          // Force a full page reload to reset React state
          setTimeout(() => {
            console.log('Redirecting with window.location');
            window.location.href = '/';
          }, 500);
        } else {
          setLoginStatus('Login failed. Please check your credentials.');
          console.log('Login failed, staying on login page');
        }
      } catch (loginError) {
        console.error('Error in login function:', loginError);
        setLoginStatus('Error connecting to server. Please try again.');
      }
    } catch (error) {
      console.error('Error during login submission:', error);
      setLoginStatus('Unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-center mb-6">Sign In</h2>
      
      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched }) => (
          <Form className="space-y-4">
            <div>
              <label htmlFor="email" className="form-label">Email Address</label>
              <Field
                id="email"
                name="email"
                type="email"
                className={`form-input ${errors.email && touched.email ? 'border-ui-error' : ''}`}
                placeholder="Enter your email"
              />
              <ErrorMessage name="email" component="div" className="form-error" />
            </div>
            
            <div>
              <label htmlFor="password" className="form-label">Password</label>
              <Field
                id="password"
                name="password"
                type="password"
                className={`form-input ${errors.password && touched.password ? 'border-ui-error' : ''}`}
                placeholder="Enter your password"
              />
              <ErrorMessage name="password" component="div" className="form-error" />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#forgot-password" className="text-secondary hover:text-secondary-dark">
                  Forgot your password?
                </a>
              </div>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full btn btn-primary ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/auth/register" className="text-secondary hover:text-secondary-dark">
                  Create an account
                </Link>
              </p>
                      <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <button onClick={(e) => {
                  e.preventDefault();
                  forceDirect();
                }} className="w-full text-xs bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded">
                  Try Direct Login (Bypass API)
                </button>
              </div>
              
              {loginStatus && (
                <div className={`mt-4 p-2 rounded-md ${loginStatus.includes('successful') ? 'bg-green-50 border border-green-200' : loginStatus.includes('Error') ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <p className={`text-xs ${loginStatus.includes('successful') ? 'text-green-600' : loginStatus.includes('Error') ? 'text-red-600' : 'text-yellow-600'}`}>
                    {loginStatus}
                  </p>
                </div>
              )}
              
              {/* Direct login button as fallback */}
              {loginStatus && loginStatus.includes('Error') && (
                <div className="mt-4">
                  <button 
                    type="button" 
                    className="w-full btn btn-secondary"
                    onClick={() => {
                      window.open('/api-test.html', '_blank');
                    }}
                  >
                    Try Login Test Page
                  </button>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default Login;